import { z } from "zod";

export const meetingTypeEnum = z.enum(["JUNTA", "ASAMBLEA", "EXTRAORDINARY"]);
export const meetingStatusEnum = z.enum(["SCHEDULED", "IN_PROGRESS", "COMPLETED", "CANCELLED"]);
export const agendaStatusEnum = z.enum(["PENDING", "DISCUSSED", "TABLED", "APPROVED", "REJECTED"]);
export const minutesStatusEnum = z.enum(["DRAFT", "UNDER_REVIEW", "APPROVED", "DISTRIBUTED"]);
export const resolutionStatusEnum = z.enum(["DRAFT", "VOTING", "APPROVED", "REJECTED"]);
export const voteChoiceEnum = z.enum(["YES", "NO", "ABSTAIN"]);

export const agendaItemInput = z.object({
  order: z.number().int().nonnegative(),
  title: z.string().min(1, "Título requerido"),
  description: z.string().optional().nullable(),
  proposedBy: z.string().optional().nullable(),
  status: agendaStatusEnum.optional(),
});

// Files and links kept with a meeting (signed acta, Fireflies transcript, recording…).
export const meetingAttachmentKindEnum = z.enum(["MINUTES_FILE", "TRANSCRIPT", "RECORDING", "CONVOCATORIA", "AGENDA", "PRESENTACION", "INFORME", "OTHER"]);
export const MEETING_ATTACHMENT_KINDS = meetingAttachmentKindEnum.options;
export type MeetingAttachmentKind = z.infer<typeof meetingAttachmentKindEnum>;
export const createMeetingAttachmentSchema = z.object({
  kind: meetingAttachmentKindEnum.default("OTHER"),
  title: z.string().trim().min(1, "Título requerido").max(200),
  // An uploaded file's URL or an external link (Fireflies, Drive, a recording).
  fileUrl: z.string().trim().url("Enlace inválido"),
});
export type CreateMeetingAttachmentInput = z.infer<typeof createMeetingAttachmentSchema>;

const meetingScheduleFields = {
  title: z.string().min(1, "Título requerido"),
  meetingType: meetingTypeEnum,
  scheduledAt: z.coerce.date(),
  // Optional so existing integrations/tests that only send a start time keep working;
  // the form always collects it now — see ics.ts for the fallback on legacy rows.
  endTime: z.coerce.date().optional().nullable(),
  location: z.string().optional().nullable(),
  meetLink: z.string().url().optional().nullable().or(z.literal("")),
  agendaFileUrl: z.string().url().optional().nullable(),
  agendaItems: z.array(agendaItemInput).optional().default([]),
  // Set when the Meet link was auto-generated via Google Calendar (keeps a handle
  // to the underlying Google event).
  googleEventId: z.string().optional().nullable(),
  // Related documents attached while creating (convocatoria, agenda, presentations…).
  // On update they are managed through /meetings/:id/attachments and ignored here.
  attachments: z.array(createMeetingAttachmentSchema).max(20).optional(),
};

const endAfterStartIssue = {
  message: "La hora de fin debe ser posterior a la hora de inicio",
  path: ["endTime"],
};

// The predicate is typed loosely (zod's inferred-object overloads otherwise choke on
// the endTime/scheduledAt comparison here); the object shapes are already validated
// by the surrounding schema, this only orders the two dates relative to each other.
const endTimeAfterStart = (d: { scheduledAt?: Date; endTime?: Date | null }): boolean =>
  !d.endTime || !d.scheduledAt || d.endTime.getTime() > d.scheduledAt.getTime();

export const createMeetingSchema = z
  .object(meetingScheduleFields)
  .refine((d: any) => endTimeAfterStart(d), endAfterStartIssue);
export type CreateMeetingInput = z.infer<typeof createMeetingSchema>;

export const updateMeetingSchema = z
  .object(meetingScheduleFields)
  .partial()
  .extend({ status: meetingStatusEnum.optional() })
  .refine((d: any) => endTimeAfterStart(d), endAfterStartIssue);

// Request to mint a Google Meet link for a meeting being created/edited.
export const generateMeetLinkSchema = z.object({
  title: z.string().min(1, "Título requerido"),
  scheduledAt: z.coerce.date(),
  // Preferred: the end time the user actually chose in the form. `durationMinutes`
  // stays only as a fallback for callers that don't have an end time yet.
  endTime: z.coerce.date().optional(),
  durationMinutes: z.coerce.number().int().positive().max(1440).optional().default(60),
});
export type GenerateMeetLinkInput = z.infer<typeof generateMeetLinkSchema>;

export const createMinutesSchema = z.object({
  draftContent: z.string().min(1, "Contenido requerido"),
});
export const updateMinutesSchema = z.object({
  draftContent: z.string().optional(),
  finalContent: z.string().optional(),
  status: minutesStatusEnum.optional(),
});
export const minutesCommentSchema = z.object({
  content: z.string().min(1, "Comentario requerido"),
});


// Documents that support a resolution (proposal, quotes, reports, drawings, the signed resolution…).
export const resolutionDocKindEnum = z.enum(["PROPUESTA", "COTIZACION", "INFORME", "PLANO", "RESOLUCION_FIRMADA", "OTRO"]);
export type ResolutionDocKind = z.infer<typeof resolutionDocKindEnum>;
export const RESOLUTION_DOC_KINDS = resolutionDocKindEnum.options;
export const createResolutionAttachmentSchema = z.object({
  kind: resolutionDocKindEnum.default("OTRO"),
  title: z.string().trim().min(1, "Título requerido").max(200),
  fileUrl: z.string().trim().url("Enlace inválido"),
});
export type CreateResolutionAttachmentInput = z.infer<typeof createResolutionAttachmentSchema>;

export const createResolutionSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  meetingId: z.string().optional().nullable(),
  proposedBy: z.string().min(1),
});
// Admin/board correction of a resolution's text — every field optional.
export const updateResolutionSchema = createResolutionSchema.partial();
export const voteSchema = z.object({
  vote: voteChoiceEnum,
  comment: z.string().optional().nullable(),
});

// Audience categories an invitation can target wholesale. The server resolves
// each one to live addresses at send time, so a category picked today still
// reaches anyone added to it before the invitation goes out.
export const meetingAudienceEnum = z.enum(["board", "admins", "owners", "residents"]);
export type MeetingAudience = z.infer<typeof meetingAudienceEnum>;
export const MEETING_AUDIENCES = meetingAudienceEnum.options;

// Invite people to a meeting: whole categories, individually picked people, and
// free-form extra guests (lawyer, auditor, contractor…) who are not in the portal.
export const inviteMeetingSchema = z
  .object({
    /** Whole categories — expanded server-side. */
    groups: z.array(meetingAudienceEnum).optional().default([]),
    /** Individually selected portal recipients. */
    emails: z.array(z.string().email("Correo inválido")).optional().default([]),
    /** Extra guests typed in by hand. */
    extraEmails: z.array(z.string().email("Correo inválido")).optional().default([]),
  })
  .refine((d) => d.groups.length + d.emails.length + d.extraEmails.length > 0, {
    message: "Seleccione al menos un destinatario",
    path: ["emails"],
  });
export type InviteMeetingInput = z.infer<typeof inviteMeetingSchema>;
